import { flushSync } from 'react-dom';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import Auth from '@Services/Auth';
import ExportService from '@Services/ExportService';
import ExportRequestManager from '@Services/ExportRequestManager';
import { EXPORT_TYPES } from '@Services/ExportService/ExportConstants';
import UserDatabases from '@Services/UserDatabases';
import { deleteProject, getConditionsExportAttempts, getProject, getProjectsPage, updateProject } from './ConditionsService';
import {
  CONDITIONS_EXPORT_SORT_FIELD,
  CONDITIONS_EXPORT_TEMPLATE,
  CONDITIONS_CONSUME_DETAIL_RESTORATION,
  CONDITIONS_CONSUME_NAVIGATOR_RESTORATION,
  CONDITIONS_DELETE_PROJECTS,
  CONDITIONS_DELETE_PROJECTS_FAILURE,
  CONDITIONS_DELETE_PROJECTS_SUCCESS,
  CONDITIONS_FETCH_DETAIL,
  CONDITIONS_FETCH_DETAIL_FAILURE,
  CONDITIONS_FETCH_DETAIL_SUCCESS,
  CONDITIONS_FETCH_PROJECTS,
  CONDITIONS_FETCH_PROJECTS_FAILURE,
  CONDITIONS_FETCH_PROJECTS_SUCCESS,
  CONDITIONS_RENAME_PROJECT,
  CONDITIONS_RENAME_PROJECT_FAILURE,
  CONDITIONS_RENAME_PROJECT_SUCCESS,
  CONDITIONS_RESET_NAVIGATOR_VIEW,
  CONDITIONS_SELECT_DETAIL,
  CONDITIONS_SET_ANNOUNCEMENT,
  CONDITIONS_SET_DETAIL_CONDITION_SET_COUNT,
  CONDITIONS_SET_DETAIL_FUNCTIONAL_GROUPS_EXPANDED,
  CONDITIONS_SET_DETAIL_RESTORATION,
  CONDITIONS_SET_DETAIL_SORT,
  CONDITIONS_SET_DETAIL_SUMMARY_TAB,
  CONDITIONS_SET_NAVIGATOR_RESTORATION,
  CONDITIONS_SET_PAGING,
  CONDITIONS_SET_SELECTED_IDS,
  CONDITIONS_SET_SORT,
  conditionsReducerKey,
} from './constants';
import { DEFAULT_SORT_BY, DEFAULT_SORT_ORDER } from './components/conditionsDetailConstants';
import { getConditionsSourceLabel } from './conditionsSourceLabel';

const BACKEND_SORT_PROPERTY = {
  date: 'created',
  name: 'name',
};

export function toBackendSort(sortItem = {}) {
  const property = BACKEND_SORT_PROPERTY[sortItem?.property] || sortItem?.property || 'created';
  const direction = sortItem?.direction || 'DESC';
  return `${property},${direction}`;
}

export const fetchConditionsProjects =
  ({ page, pageSize, sort }) =>
  async (dispatch) => {
    dispatch({ type: CONDITIONS_FETCH_PROJECTS });
    try {
      const projectsPage = await getProjectsPage({ page, size: pageSize, sort: toBackendSort(sort?.[0]) });
      dispatch({ type: CONDITIONS_FETCH_PROJECTS_SUCCESS, payload: projectsPage });

      if (projectsPage.totalItems === 0) {
        flushSync(() => {
          history.push({ pathname: paths.CONDITIONS_DRAW, state: { noProjects: true } });
        });
      }
    } catch (requestError) {
      Logger.error('ConditionsNavigator load projects failed with error:', requestError);
      dispatch({ type: CONDITIONS_FETCH_PROJECTS_FAILURE, payload: requestError });
    }
  };

export const setConditionsSort = (sortString) => {
  const [property, direction] = sortString?.split(',') || [];
  return {
    type: CONDITIONS_SET_SORT,
    payload: [{ property, direction }],
  };
};

export const setConditionsPaging = ({ page, size }, currentPageSize) => {
  if (size !== undefined && size !== currentPageSize) {
    return {
      type: CONDITIONS_SET_PAGING,
      payload: { pageSize: size, page: 0 },
    };
  }

  return {
    type: CONDITIONS_SET_PAGING,
    payload: page !== undefined ? { page } : {},
  };
};

export const setConditionsSelectedIds = (selectedIds) => ({
  type: CONDITIONS_SET_SELECTED_IDS,
  payload: selectedIds,
});

export const setConditionsAnnouncement = (announcement) => ({
  type: CONDITIONS_SET_ANNOUNCEMENT,
  payload: announcement,
});

export const resetConditionsNavigatorView = () => ({
  type: CONDITIONS_RESET_NAVIGATOR_VIEW,
});

export const renameConditionsProject = (id, newName) => async (dispatch) => {
  if (!newName || !String(newName).trim()) return false;

  dispatch({ type: CONDITIONS_RENAME_PROJECT });
  try {
    const updatedRun = await updateProject(id, { name: newName.trim() });
    dispatch({ type: CONDITIONS_RENAME_PROJECT_SUCCESS, payload: { id, updatedRun } });
    return true;
  } catch (requestError) {
    Logger.error('ConditionsNavigator rename project failed with error:', requestError);
    dispatch({ type: CONDITIONS_RENAME_PROJECT_FAILURE, payload: requestError });
    return false;
  }
};

export const deleteConditionsProjects = (idsToDelete) => async (dispatch) => {
  dispatch({ type: CONDITIONS_DELETE_PROJECTS });
  try {
    await Promise.all(idsToDelete.map((id) => deleteProject(id)));
    dispatch({ type: CONDITIONS_DELETE_PROJECTS_SUCCESS, payload: { ids: idsToDelete } });
  } catch (requestError) {
    Logger.error('ConditionsNavigator delete projects failed with error:', requestError);
    dispatch({ type: CONDITIONS_DELETE_PROJECTS_FAILURE, payload: requestError });
  }
};

function getCachedDetailByProjectId(state, id) {
  const conditionsState = state?.get?.(conditionsReducerKey) || state?.[conditionsReducerKey] || {};
  return conditionsState.detailByProjectId?.[id] || null;
}

export const fetchConditionsDetail = (id) => async (dispatch, getState) => {
  if (getCachedDetailByProjectId(getState?.(), id)) {
    dispatch({ type: CONDITIONS_SELECT_DETAIL, payload: { id } });
    return;
  }

  dispatch({ type: CONDITIONS_FETCH_DETAIL, payload: { id } });
  try {
    const projectData = await getProject(id);
    dispatch({ type: CONDITIONS_FETCH_DETAIL_SUCCESS, payload: { id, ...projectData } });
  } catch (requestError) {
    Logger.error('ConditionsDetail load project failed with error:', requestError);
    dispatch({ type: CONDITIONS_FETCH_DETAIL_FAILURE, payload: requestError });
  }
};

export const setConditionsDetailSort = (projectId, sortBy, sortOrder) => ({
  type: CONDITIONS_SET_DETAIL_SORT,
  payload: { projectId, sortBy, sortOrder },
});

export const setConditionsDetailConditionSetCount = (projectId, count) => ({
  type: CONDITIONS_SET_DETAIL_CONDITION_SET_COUNT,
  payload: { projectId, count },
});

export const setConditionsDetailFunctionalGroupsExpanded = (projectId, rank, isExpanded) => ({
  type: CONDITIONS_SET_DETAIL_FUNCTIONAL_GROUPS_EXPANDED,
  payload: { projectId, rank, isExpanded },
});

export const setConditionsDetailSummaryTab = (projectId, chartKey, tabIndex) => ({
  type: CONDITIONS_SET_DETAIL_SUMMARY_TAB,
  payload: { projectId, chartKey, tabIndex },
});

export const setNavigatorRestoration = (payload) => ({
  type: CONDITIONS_SET_NAVIGATOR_RESTORATION,
  payload,
});

export const consumeNavigatorRestoration = () => ({
  type: CONDITIONS_CONSUME_NAVIGATOR_RESTORATION,
});

export const setDetailRestoration = (payload) => ({
  type: CONDITIONS_SET_DETAIL_RESTORATION,
  payload,
});

export const consumeDetailRestoration = () => ({
  type: CONDITIONS_CONSUME_DETAIL_RESTORATION,
});

export function getConditionsExportSortField(sortBy) {
  return CONDITIONS_EXPORT_SORT_FIELD[sortBy] || CONDITIONS_EXPORT_SORT_FIELD[DEFAULT_SORT_BY];
}

export function buildConditionsExportPayload({
  projectId,
  docFormat,
  databaseId = UserDatabases.getDefaultDatabase()?.id,
  detailSortBy = DEFAULT_SORT_BY,
  detailSortOrder = DEFAULT_SORT_ORDER,
  run = {},
} = {}) {
  return {
    template: CONDITIONS_EXPORT_TEMPLATE,
    projectId,
    docFormat,
    databaseId,
    sortField: getConditionsExportSortField(detailSortBy),
    sortOrder: detailSortOrder,
    headline: getConditionsSourceLabel(run),
  };
}

export const requestConditionsExportDialog = () => async () => {
  const enableAnonymousExports = Auth.getUser()?.details?.enableAnonymousExports;
  if (Auth.isAnonymousUser() && !enableAnonymousExports) {
    ExportService.openLoginDialog({
      trackingName: 'conditions:export:sign-in',
      postLoginRedirect: { pathname: paths.CONDITIONS },
    });
    return { canOpen: false };
  }

  try {
    const { exportAttemptsLimit, exportAttemptsCurrentlyUsed } = await getConditionsExportAttempts();
    const nextExportAttempt = Number.isFinite(exportAttemptsCurrentlyUsed) ? exportAttemptsCurrentlyUsed + 1 : undefined;

    if (Number.isFinite(exportAttemptsLimit) && Number.isFinite(nextExportAttempt) && nextExportAttempt > exportAttemptsLimit) {
      ExportService.openDailyExportLimitReachedModal();
      return { canOpen: false };
    }

    return {
      canOpen: true,
      exportAttemptsLimit,
      exportAttemptsCurrentlyUsed: nextExportAttempt,
    };
  } catch (requestError) {
    Logger.error('Conditions export attempts request failed with error:', requestError);
    return { canOpen: false };
  }
};

export const exportConditionsProject = (params) => () => {
  const payload = buildConditionsExportPayload(params);
  ExportRequestManager.sendExportRequest(EXPORT_TYPES.GENERIC, payload, {});
  return payload;
};
